import * as React from 'react';
// mui
import {
  Box,
  IconButton,
  List,
  ListSubheader,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Skeleton
} from '@mui/material';
// icons
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BugReportIcon from '@mui/icons-material/BugReport';
import AddCommentIcon from '@mui/icons-material/AddComment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SettingsIcon from '@mui/icons-material/Settings';
// router
import { useNavigate } from 'react-router';
// redux
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { selectSelectedProjectId, selectAllProjects } from '../../store/selectors/projectSelectors';
import { setSelectedProject, setProjects, setBaseProjectId } from '../../store/slices/projectsSlice';
// types
import Project from '../../interfaces/Project.interface';
// axios
import api from '../../axios';
// components
import RenameProjectDialog from './RenameProjectDialog';
import AddProjectDialog from './AddProjectDialog';
import DeleteProjectDialog from './DeleteProjectDialog';

interface LayoutMenuProps {
  onClickCallback?: () => void; // for mobile to close the drawer
}

export default function LayoutMenu(props: LayoutMenuProps) {
  // props
  const onClickCallback = props.onClickCallback;

  // hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // redux
  const dispatch = useDispatch<AppDispatch>();
  const localSelectedProjectId: number | null = useSelector(selectSelectedProjectId());
  const localProjects: Project[] = useSelector(selectAllProjects());

  // local state
  const [ projectMenuAnchorEl, setProjectMenuAnchorEl ] = React.useState<null | HTMLElement>(null);

  // dialog states
  const [ showAddProjectDialog, setShowAddProjectDialog ] = React.useState<boolean>(false);
  const [ showRenameProjectDialog, setShowRenameProjectDialog ] = React.useState<boolean>(false);
  const [ showConfirmProjectDeleteDialog, setShowConfirmProjectDeleteDialog ] = React.useState<boolean>(false);
  
  // focused project is the project the user is clicking on (not the selected project)
  const [ focusedProject, setFocusedProject ] = React.useState<Project | null>(null);

  // navigate functions
  const linkTo = (page: string) => {
    if (onClickCallback) { onClickCallback(); }
    navigate(`/app/${page}`);
  }
  
  const linkToTasksByProject = (projectId: number | null) => {
    dispatch(setSelectedProject(projectId));

    // if the user is not on the tasks page, navigate there
    if (!window.location.href.includes('tasks')) {
      navigate('/app/tasks');
    }

    if (onClickCallback) { onClickCallback(); }
  }

  // when the user clicks on a task project
  const handleProjectDropdownButtonClick = (event: React.MouseEvent<HTMLButtonElement>, project: Project) => {
    event.stopPropagation();
    setProjectMenuAnchorEl(event.currentTarget);
    setFocusedProject(project);
  };

  const fetchProjects = () => {
    api.get('/projects/get')
      .then((res) => {
        console.log(res);
        const data: Project[] = res.data;
        dispatch(setProjects(data));

        const baseProject = data.find((current: Project) => current.isBaseProject);
        if (baseProject) {
          dispatch(setSelectedProject(baseProject.id));
          dispatch(setBaseProjectId(baseProject.id));
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  React.useEffect(() => {
    fetchProjects();
  }, []);

  return (
    <Box sx={{ overflow: 'auto' }}>
      <List
        sx={{ background: isMobile ? theme.palette.background.default : '' }}
        subheader={
          <ListSubheader sx={{ background: isMobile ? theme.palette.background.default : '' }} component="div">
            My Projects
          </ListSubheader>
        }
      >
        { localProjects.length ? <>
          {localProjects.map((project: Project) => (
            <ListItemButton
              key={project.id}
              dense
              onClick={() => linkToTasksByProject(project.id)}
              sx={{ pl: 4 }}
              selected={window.location.href.includes('tasks') && localSelectedProjectId === project.id}
            >
              <ListItemText primary={project.name} />
              <ListItemIcon sx={{ minWidth: '24px' }}>
                <IconButton disabled={project.isBaseProject} onClick={(e) => { handleProjectDropdownButtonClick(e, project) }}>
                  <MoreVertIcon />
                </IconButton>
              </ListItemIcon>
            </ListItemButton>
          ))}
        </> : <>
          <Skeleton variant="rounded" sx={{ mx: 2, mb: 1 }} height={40} />
          <Skeleton variant="rounded" sx={{ mx: 2, mb: 1 }} height={40} />
          <Skeleton variant="rounded" sx={{ mx: 2, mb: 1 }} height={40} />
        </>}
        
        <ListItemButton
          sx={{ pl: 4 }}
          onClick={() => { setShowAddProjectDialog(true); }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <AddIcon />
          </ListItemIcon>
          <ListItemText sx={{ color: 'text.secondary' }} secondary={`Add Project`} />
        </ListItemButton>

        <Menu
          anchorEl={projectMenuAnchorEl}
          open={Boolean(projectMenuAnchorEl)}
          onClose={() => { setProjectMenuAnchorEl(null); }}
        >
          <MenuItem onClick={() => { setProjectMenuAnchorEl(null); setShowRenameProjectDialog(true); }}>Rename</MenuItem>
          <MenuItem onClick={() => { setProjectMenuAnchorEl(null); setShowConfirmProjectDeleteDialog(true); }}>Delete</MenuItem>
        </Menu>
      </List>

      <List
        sx={{ background: isMobile ? theme.palette.background.default : '' }}
        subheader={
          <ListSubheader sx={{ background: isMobile ? theme.palette.background.default : '' }} component="div">
            Pages
          </ListSubheader>
        }
      >
        <ListItem disablePadding>
          <ListItemButton
            selected={window.location.href.includes('todo')}
            onClick={() => linkTo('todo')}
            sx={{ pl: 2 }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <ArrowForwardIcon />
            </ListItemIcon>
            <ListItemText primary="To Do" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            selected={window.location.href.includes('upcoming')}
            onClick={() => linkTo('upcoming')}
            sx={{ pl: 2 }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <CalendarMonthIcon />
            </ListItemIcon>
            <ListItemText primary="Upcoming" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            selected={window.location.href.includes('dashboard')}
            onClick={() => linkTo('dashboard')}
            sx={{ pl: 2 }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <ShowChartIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            selected={window.location.href.includes('settings')}
            onClick={() => linkTo('settings')}
            sx={{ pl: 2 }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItemButton>
        </ListItem>

        <ListSubheader component="div">
          Alpha Testers
        </ListSubheader>

        <ListItem disablePadding>
          <ListItemButton
            selected={window.location.href.includes('bugreport')}
            onClick={() => linkTo('bugreport')}
            sx={{ pl: 2 }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <BugReportIcon />
            </ListItemIcon>
            <ListItemText primary="Report Bug" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            selected={window.location.href.includes('feedback')}
            onClick={() => linkTo('feedback')}
            sx={{ pl: 2 }}
          >
            <ListItemIcon sx={{ minWidth: '40px' }}>
              <AddCommentIcon />
            </ListItemIcon>
            <ListItemText primary="Feedback" />
          </ListItemButton>
        </ListItem>
      </List>

      { showConfirmProjectDeleteDialog && (
        <DeleteProjectDialog
          focusedProject={focusedProject}
          onClose={() => { setShowConfirmProjectDeleteDialog(false); }}
          onDelete={(id) => { linkToTasksByProject(id); }}
        />
      )}

      { showAddProjectDialog && (
        <AddProjectDialog
          onClose={() => { setShowAddProjectDialog(false); }}
          onSave={(id) => { setShowAddProjectDialog(false); linkToTasksByProject(id); }}
        />
      )}

      { showRenameProjectDialog && (
        <RenameProjectDialog
          focusedProject={focusedProject}
          onClose={() => { setShowRenameProjectDialog(false); }}
        />
      )}
    </Box>
  )
}

